import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Avatar } from "../badges";
import { toast } from "sonner";
import { Send } from "lucide-react";

export default function DiscussionsTab({ project }) {
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [body, setBody] = useState("");

  const load = () => {
    api.get("/comments", { params: { entity_type: "project", entity_id: project.id } }).then((r) => setComments(r.data));
    api.get("/activities", { params: { project_id: project.id, limit: 40 } }).then((r) => setActivities(r.data));
  };
  useEffect(load, [project.id]); // eslint-disable-line

  const post = async () => {
    if (!body.trim()) return;
    try {
      await api.post("/comments", { entity_type: "project", entity_id: project.id, body });
      setBody(""); load();
      toast.success("Posted");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="discussions-tab-content">
      <div className="space-y-4">
        <h3 className="font-heading font-semibold">Project Discussion</h3>
        <Card className="p-4 space-y-3">
          <Textarea data-testid="discussion-input" placeholder="Share an update, decision, or question with the team..."
            value={body} onChange={(e) => setBody(e.target.value)} className="min-h-[80px]" />
          <Button size="sm" data-testid="discussion-submit-btn" onClick={post} disabled={!body.trim()}>
            <Send className="h-3.5 w-3.5 mr-1" /> Post
          </Button>
        </Card>
        <div className="space-y-3">
          {[...comments].reverse().map((c) => (
            <Card key={c.id} className="p-4" data-testid="discussion-item">
              <div className="flex items-center gap-2">
                <Avatar name={c.author_name} size="sm" />
                <p className="text-sm font-medium">{c.author_name}</p>
                <span className="text-[10px] font-mono uppercase text-muted-foreground border border-border rounded px-1.5">{c.author_role}</span>
                <span className="text-[10px] font-mono text-muted-foreground ml-auto">{new Date(c.created_at).toLocaleString()}</span>
              </div>
              <p className="text-sm mt-2 leading-relaxed">{c.body}</p>
            </Card>
          ))}
          {!comments.length && <p className="text-sm text-muted-foreground">No discussions yet. Start one above.</p>}
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-heading font-semibold">Complete Activity Log</h3>
        <Card className="divide-y divide-border max-h-[70vh] overflow-y-auto">
          {activities.map((a) => (
            <div key={a.id} className="p-3 flex items-start gap-2.5 text-sm" data-testid="log-activity-item">
              <Avatar name={a.actor_name} size="xs" />
              <div className="min-w-0">
                <p>
                  <span className="font-medium">{a.actor_name}</span>{" "}
                  <span className="text-muted-foreground">{a.action}</span>{" "}
                  <span className="font-mono text-xs text-primary">{a.entity_ref}</span>
                </p>
                {a.detail && <p className="text-xs text-muted-foreground truncate">{a.detail}</p>}
                <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{new Date(a.created_at).toLocaleString()}</p>
              </div>
            </div>
          ))}
          {!activities.length && <p className="p-4 text-sm text-muted-foreground">No activity yet.</p>}
        </Card>
      </div>
    </div>
  );
}
